/**
 * The typed API_CONTRACT_SV_v0.1 §2 client — one function per endpoint, shapes byte-for-byte.
 * Base `/api/v1/sv/capture`, case-scoped bearer from the link exchange (client.ts).
 */

import { CAPTURE_BASE, request } from './client';

export type { ConsentPurposeHint, ExchangeOutcome, ExchangeResult, ExchangeSummary, OtpChallenge, ServedBranding, ServedStamp, SvNotice } from './client';
export { CAPTURE_BASE, exchangeLinkToken, isOtpChallenge, verifyLinkOtp } from './client';

// ── header names (shared with the chain module and the mock's server-side recompute) ─────────
export const H_CHAIN_HASH = 'X-SV-Chain-Hash';
export const H_CHUNK_SHA256 = 'X-SV-Chunk-Sha256';
export const H_CAPTURED_AT = 'X-SV-Captured-At';
export const H_PROMPT_ID = 'X-SV-Prompt-Id';
export const H_GEO = 'X-SV-Geo';
export const H_IMU = 'X-SV-IMU';

// ── POST /consent ──────────────────────────────────────────────────────────────────────────────
export interface ConsentPurposeGrant {
  readonly key: string;
  readonly granted: boolean;
}
export interface ConsentBody {
  readonly noticeVersion: string;
  readonly purposes: readonly ConsentPurposeGrant[];
}
export interface ConsentResponse {
  readonly recordedAt: string;
}
export function postConsent(body: ConsentBody): Promise<ConsentResponse> {
  return request<ConsentResponse>(`${CAPTURE_BASE}/consent`, { method: 'POST', body });
}

// ── POST /start ────────────────────────────────────────────────────────────────────────────────
export type ShotKind = 'STILL' | 'CLIP';
export type ShotOrientation = 'LANDSCAPE' | 'PORTRAIT' | 'ANY';
export type ChallengeType = 'SHOW_NONCE' | 'SPEAK_NONCE' | 'ACTION';
export interface ShotChallenge {
  readonly type: ChallengeType;
  readonly text: string;
}
export interface ScriptShot {
  readonly shotId: string;
  readonly ordinal: number;
  readonly kind: ShotKind;
  readonly promptKey: string;
  /** The `en` value, kept by the be for older readers. Render through `shotText()` (FSD-18 D1), never directly. */
  readonly promptText: string;
  /**
   * The localised text of record, `{en, hi, …}` (be M6, `d0af889`): copied verbatim from the template row (or the
   * challenge-bank entry) onto every issued shot and covered by `script_sha256`. Absent on a pre-M6 be.
   */
  readonly promptTexts?: Readonly<Record<string, string>>;
  readonly orientation: ShotOrientation;
  readonly minDurationS: number;
  readonly maxDurationS: number;
  readonly geoToleranceM: number;
  readonly mandatory: boolean;
  readonly challenge?: ShotChallenge;
  /**
   * F-S7 (be eebbcf9b) — on the ID_DOCUMENT row of a case whose frozen policy runs no India registry rail (a Gulf /
   * tenant-pack case) the be says `NOT_APPLICABLE_POLICY`: it never runs the Aadhaar mask there, so the step says nothing
   * about Aadhaar or masking. Inside the hashed script; absent on every India row (byte-unchanged).
   */
  readonly masking?: 'NOT_APPLICABLE_POLICY' | string | null;
}
export interface StartResponse {
  readonly visitId: string;
  readonly nonce: string;
  readonly serverTime: string;
  readonly script: readonly ScriptShot[];
}
export function startCapture(): Promise<StartResponse> {
  return request<StartResponse>(`${CAPTURE_BASE}/start`, { method: 'POST' });
}

// ── POST /geo ──────────────────────────────────────────────────────────────────────────────────
export type GeoPermissionState = 'GRANTED' | 'DENIED' | 'TIMEOUT' | 'UNSUPPORTED' | 'ERROR';
/** lat/lng/accuracyM are required for a GRANTED row; a fix-less row carries `permissionState` (the be
 *  records the honest absence and still derives the source-IP leg from the request). */
export interface GeoBody {
  readonly lat?: number;
  readonly lng?: number;
  readonly accuracyM?: number;
  readonly permissionState?: GeoPermissionState;
  readonly provider?: string;
  readonly collectedAt: string;
  readonly mockFlag?: true | false | null;
  readonly altitudeM?: number;
  readonly speedMps?: number;
}
export interface GeoResponse {
  readonly geoRowId: string;
}
export function postGeo(body: GeoBody): Promise<GeoResponse> {
  return request<GeoResponse>(`${CAPTURE_BASE}/geo`, { method: 'POST', body });
}

// ── PUT /shots/{shotId}/chunks/{seq} ───────────────────────────────────────────────────────────
export interface ChunkHeaders {
  readonly chainHash: string;
  readonly chunkSha256: string;
  readonly capturedAt: string;
  readonly promptId: string;
  /** The EXACT header string bound into the chain, or null when the header is omitted. */
  readonly geo: string | null;
  readonly imu: string | null;
}
export function chunkHeaderRecord(h: ChunkHeaders): Record<string, string> {
  const out: Record<string, string> = {
    [H_CHAIN_HASH]: h.chainHash,
    [H_CHUNK_SHA256]: h.chunkSha256,
    [H_CAPTURED_AT]: h.capturedAt,
    [H_PROMPT_ID]: h.promptId,
  };
  if (h.geo !== null) out[H_GEO] = h.geo;
  if (h.imu !== null) out[H_IMU] = h.imu;
  return out;
}
export async function putChunk(
  shotId: string,
  seq: number,
  bytes: Uint8Array,
  headers: ChunkHeaders,
): Promise<void> {
  await request<null>(
    `${CAPTURE_BASE}/shots/${encodeURIComponent(shotId)}/chunks/${seq}`,
    { method: 'PUT', body: bytes, headers: chunkHeaderRecord(headers) },
  );
}

// ── POST /shots/{shotId}/complete ──────────────────────────────────────────────────────────────
export interface ShotSignature {
  readonly type: 'WEBAUTHN' | 'KEYSTORE';
  readonly payload: unknown;
}
export interface CompleteShotBody {
  readonly chunkCount: number;
  readonly finalChainHash: string;
  readonly mimeType: string;
  readonly durationS?: number;
  readonly signature?: ShotSignature;
}
export interface CompleteShotResponse {
  readonly serverSha256: string;
  readonly sizeBytes: number;
  readonly acceptedAt: string;
}
export function completeShot(shotId: string, body: CompleteShotBody): Promise<CompleteShotResponse> {
  return request<CompleteShotResponse>(
    `${CAPTURE_BASE}/shots/${encodeURIComponent(shotId)}/complete`,
    { method: 'POST', body },
  );
}

// ── POST /telemetry ────────────────────────────────────────────────────────────────────────────
export interface TelemetryBody {
  /** Omitted when nothing was collected (the be keeps the shot's earlier fingerprint); never `null` on submit. */
  readonly fingerprint?: unknown;
  readonly imuSummary?: unknown;
  readonly permissions?: Readonly<Record<string, string | boolean | null>>;
  readonly clientTime: string;
}
// ── SPEC_CAPTURE_UX_R2 §2.2 / §7.3 — contract v0.1.13 §11a (be #172 `c666786`, V178). Shapes pinned to the be's wire dump
// `__tests__/fixtures/sv-ux-r2.json` (sha256 8e56b228…, `uxR2Dumps.test.ts`); the mock adapter serves the dump's rows.

/**
 * `PATCH /locale {locale}` → `200 {customerLocale, changed, reauthRequired, reauthRequiredAt}` (dump `locale_patch`).
 * Idempotent (`changed:false` on the same value); the case comes from the bearer. `422 SV_VALIDATION` outside the
 * eleven-row vocabulary (`locale_refusal` — the value is never echoed); `409 SV_CASE_NOT_CAPTURABLE` on a terminal case.
 */
export interface LocaleSet {
  readonly customerLocale: string;
  readonly changed: boolean;
  readonly reauthRequired: boolean;
  readonly reauthRequiredAt: string | null;
}
export async function patchLocale(locale: string): Promise<LocaleSet> {
  return request<LocaleSet>(`${CAPTURE_BASE}/locale`, { method: 'PATCH', body: { locale } });
}

/**
 * `POST /help-request {reason?, step?}` → `202 {helpRequestId, status:"OPEN", requestedAt, message, reauthRequired,
 * reauthRequiredAt}` (dump `help_request_202`): the row lands on the tenant's ops queue and `message` is the receipt the
 * surface shows VERBATIM — a call, never a promised time. `422 SV_VALIDATION` on a reason over 280 code points or a
 * malformed step (refused before the bucket is charged); `429 SV_RATE_LIMITED` (per-case bucket: three taps, then one
 * every five minutes — the surface shows the server's words and keeps the control); `409 SV_CASE_NOT_CAPTURABLE` on a
 * terminal case. Opens no live room.
 */
export interface HelpRequested {
  readonly helpRequestId: string;
  readonly status: string;
  readonly requestedAt: string;
  readonly message: string;
  readonly reauthRequired: boolean;
  readonly reauthRequiredAt: string | null;
}
export async function postHelpRequest(body: { reason?: string; step?: string }): Promise<HelpRequested> {
  return request<HelpRequested>(`${CAPTURE_BASE}/help-request`, { method: 'POST', body });
}

export async function postTelemetry(body: TelemetryBody): Promise<void> {
  await request<null>(`${CAPTURE_BASE}/telemetry`, { method: 'POST', body });
}

// ── POST /submit ───────────────────────────────────────────────────────────────────────────────
export interface SubmitResponse {
  readonly status: 'SUBMITTED';
  readonly receiptRef: string;
}
export function submitCapture(): Promise<SubmitResponse> {
  return request<SubmitResponse>(`${CAPTURE_BASE}/submit`, { method: 'POST' });
}

// ── GET /state ─────────────────────────────────────────────────────────────────────────────────
/** SV-5-2: the representative's view of the case's live leg — present on a be ≥ SV-5-2, absent on an older be. */
export interface CaptureStateLiveLeg {
  /** LIVE_REQUESTED with a bound call session: the surface offers "Join the live verification". */
  readonly joinable: boolean;
  readonly sessionId: string | null;
  /** SV-5-2 second round (WIRE_CONTRACT_SV52 §3): true while the case has NO recorded consent granting all three live
   *  purposes (LIVE_VIDEO, LIVE_IDENTITY_CHECK, LIVE_CUSTODY_RETENTION) — the join must pass through the ceremony first.
   *  Absent on an older be ⇒ treated as not required (the be still refuses the join with SV_CONSENT_LIVE_REQUIRED). */
  readonly consentRequired?: boolean;
  /** The notice version whose LIVE_LEG section the re-consent is taken against (`SITE_VERIFICATION_CUSTOMER@v2`). */
  readonly liveNoticeVersion?: string | null;
}
export interface CaptureState {
  readonly status: string;
  readonly completedShots: readonly string[];
  readonly remaining: readonly string[];
  readonly recaptureOnly?: readonly string[];
  readonly liveLeg?: CaptureStateLiveLeg;
}
export function getCaptureState(): Promise<CaptureState> {
  return request<CaptureState>(`${CAPTURE_BASE}/state`);
}

// ── SV-5-2: the live leg (DEC-089 ruling 8) ────────────────────────────────────────────────────
// Shapes vendored from the be's real wire dump (build/wire/sv-live-join.json → __tests__/fixtures) — the same
// grant shape the data room's customer join returns, minus `role` (always the customer side), plus `sessionId`.

/** POST /live/join → the LiveKit join grant. 409 SV_LIVE_NOT_CLAIMED {retryAfterS} while no officer has taken the
 *  leg; 409 SV_LIVE_ENDED once it is over; 409 SV_LIVE_NOT_REQUESTED on a case with no leg. */
export interface LiveJoinGrant {
  /** wss signalling URL — supplied by the backend, never hardcoded (the droom rule). */
  readonly url: string;
  /** Short-lived, server-minted room access token (join + publish + subscribe, pinned to this room). */
  readonly token: string;
  readonly roomName: string;
  readonly sessionId: string;
  readonly identity: string;
}
export function joinLive(): Promise<LiveJoinGrant> {
  return request<LiveJoinGrant>(`${CAPTURE_BASE}/live/join`, { method: 'POST' });
}

/** SV-5-2 second round — one officer-issued instruction prompt as the heartbeat serves it (WIRE_CONTRACT_SV52 §3).
 *  QUEUED rows are never served (deferred to the segment safe point); at most ONE row is PENDING/ACKNOWLEDGED — the
 *  card — and the rest are history, oldest first. `kind` drives the representative's instruction heading; `text` is
 *  already locale-resolved by the be, `textEn`/`textHi` let the client follow a locale switch mid-call. */
export type LivePromptKind = 'PAN' | 'HOLD' | 'WALK' | 'READ' | string;
export type LivePromptState = 'PENDING' | 'ACKNOWLEDGED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED' | string;
export interface LivePrompt {
  readonly promptId: string;
  readonly promptKey: string;
  readonly promptClass: 'PROMPTED' | string;
  readonly kind: LivePromptKind;
  readonly phase: string;
  readonly text: string;
  readonly textEn?: string | null;
  readonly textHi?: string | null;
  readonly challenge?: boolean;
  readonly state: LivePromptState;
  readonly issuedAt: string | null;
  readonly segmentSeq?: number | null;
}
/** The states that make a row the ACTIVE card (shown with a Done button); everything else is history. */
export function isActiveLivePrompt(p: LivePrompt): boolean {
  return p.state === 'PENDING' || p.state === 'ACKNOWLEDGED';
}

/** POST /live/prompts/{promptId}/ack → the row (state ACKNOWLEDGED). Called ONCE when the card is shown. */
export function ackLivePrompt(promptId: string): Promise<LivePrompt> {
  return request<LivePrompt>(`${CAPTURE_BASE}/live/prompts/${encodeURIComponent(promptId)}/ack`, { method: 'POST' });
}

/** POST /live/prompts/{promptId}/answer → the "done" tap. `promoted` is the next queued prompt now issued (the segment
 *  safe point was reached), or null. 404 unknown prompt; 409 SV_LIVE_PROMPT_CLOSED when the row is already terminal. */
export interface LiveAnswerResponse {
  readonly prompt: LivePrompt;
  readonly promoted?: LivePrompt | null;
}
export function answerLivePrompt(promptId: string): Promise<LiveAnswerResponse> {
  return request<LiveAnswerResponse>(`${CAPTURE_BASE}/live/prompts/${encodeURIComponent(promptId)}/answer`, { method: 'POST' });
}

export type LiveLegState = 'WAITING_CLAIM' | 'LIVE' | 'ENDED' | 'FAILED';
/** POST /live/heartbeat → where the leg is now + the prompts to render. Sent every ~10 s while on the live step. */
export interface LiveHeartbeat {
  readonly state: LiveLegState | string;
  readonly sessionId: string;
  readonly sessionStatus: string;
  readonly presenceAt: string | null;
  readonly caseStatus: string;
  /** Oldest first; at most one active row (see isActiveLivePrompt). Absent on an older be ⇒ treated as []. */
  readonly prompts?: readonly LivePrompt[];
}
export function liveHeartbeat(): Promise<LiveHeartbeat> {
  return request<LiveHeartbeat>(`${CAPTURE_BASE}/live/heartbeat`, { method: 'POST' });
}
